'use client';

import { ExternalLink, Loader2, Monitor, Smartphone } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { useCheckoutPreviewFrame } from '@/components/setup/experience/hooks/useCheckoutPreviewFrame';
import { useSetupShippingSummary } from '@/components/setup/experience/hooks/useSetupShippingSummary';
import {
  buildSetupProductsListInput,
  SETUP_CATALOG_LIST_PAGE_SIZE,
} from '@/components/setup/experience/lib/setupCatalogSummary';
import {
  buildCheckoutProductOptions,
  type SetupCheckoutLineItem,
} from '@/components/setup/experience/lib/setupCheckoutLineItem';
import {
  buildSetupCheckoutPreviewUrl,
  SETUP_CHECKOUT_IFRAME_SANDBOX,
} from '@/components/setup/experience/lib/setupCheckoutUtils';
import { useProductsList } from '@/lib/api/products';
import { trackSetupCheckoutPreviewShown } from '@/lib/setupAnalytics';
import { cn } from '@/lib/utils';

/** Toggle between a full-bleed desktop preview and a phone-width preview. */
type SetupPreviewViewport = 'web' | 'mobile';

/** Phone-width canvas (px) — matches a modern device's logical viewport so the checkout reflows to its mobile layout. */
const MOBILE_PREVIEW_WIDTH = 390;

interface SetupCheckoutPreviewPanelProps {
  storeId: string;
  baseCurrency: string;
  funnelId: string | null;
  checkoutUrl: string | null;
  shippingSnapshot?: { previewCountryCode?: string | null } | null;
  /** A background catalog sync is still importing — surface an importing hint instead of the preview. */
  isSyncing?: boolean;
}

interface CheckoutPreviewFrameProps {
  previewUrl: string;
  title: string;
  className?: string;
  onLoad: () => void;
}

function CheckoutPreviewFrame({ previewUrl, title, className, onLoad }: CheckoutPreviewFrameProps) {
  return (
    <iframe
      key={previewUrl}
      title={title}
      src={previewUrl}
      className={className}
      sandbox={SETUP_CHECKOUT_IFRAME_SANDBOX}
      onLoad={onLoad}
    />
  );
}

const VIEWPORT_BUTTON_BASE =
  'inline-flex h-full items-center gap-1.5 rounded-md px-2.5 text-[0.6875rem] font-semibold transition-[background-color,color,box-shadow] duration-150 motion-reduce:transition-none';
const VIEWPORT_BUTTON_ACTIVE =
  'bg-card text-foreground shadow-[0_1px_2px_color-mix(in_oklch,var(--foreground)_14%,transparent)]';
const VIEWPORT_BUTTON_INACTIVE = 'text-muted-foreground hover:text-foreground';

export function SetupCheckoutPreviewPanel({
  storeId,
  baseCurrency,
  funnelId,
  checkoutUrl,
  shippingSnapshot,
  isSyncing = false,
}: SetupCheckoutPreviewPanelProps) {
  const { t, i18n } = useTranslation(['setup', 'shippings']);
  const xp = 'experience.checkout';
  const previewXp = 'experience.checkoutPreview';

  const [lineItem, setLineItem] = React.useState<SetupCheckoutLineItem | null>(null);
  const [viewport, setViewport] = React.useState<SetupPreviewViewport>('web');
  const previewTrackedRef = React.useRef<string | null>(null);

  const isLaunched = Boolean(funnelId);
  const isMobile = viewport === 'mobile';

  const { rates: shippingRates } = useSetupShippingSummary(
    storeId,
    baseCurrency,
    t('experience.shipping.freeSummary'),
    t('shippings:table.allCountries'),
    Boolean(storeId),
  );

  const { data: productsData } = useProductsList(
    buildSetupProductsListInput(storeId, SETUP_CATALOG_LIST_PAGE_SIZE),
    { enabled: Boolean(storeId) },
  );

  const productOptions = React.useMemo(
    () => buildCheckoutProductOptions(productsData?.items, baseCurrency),
    [baseCurrency, productsData?.items],
  );

  // The product chooser was removed — auto-load the catalog's first sellable product so the
  // preview renders without any merchant input.
  React.useEffect(() => {
    if (lineItem || productOptions.length === 0) return;
    setLineItem(productOptions[0].lineItem);
  }, [lineItem, productOptions]);

  const previewUrl = React.useMemo(() => {
    if (!lineItem || !funnelId) return null;
    return buildSetupCheckoutPreviewUrl({
      storeId,
      funnelId,
      currency: baseCurrency,
      checkoutUrl,
      lineItem,
      i18nLanguage: i18n.language,
      shippingSnapshot,
      shippingRates,
    });
  }, [
    baseCurrency,
    checkoutUrl,
    funnelId,
    i18n.language,
    lineItem,
    shippingRates,
    shippingSnapshot,
    storeId,
  ]);

  const showLiveIframe = Boolean(isLaunched && lineItem && previewUrl);

  const inlineFrame = useCheckoutPreviewFrame({ previewUrl, enabled: showLiveIframe });

  const trackPreviewShown = React.useCallback(
    (url: string) => {
      if (previewTrackedRef.current === url) return;
      previewTrackedRef.current = url;
      trackSetupCheckoutPreviewShown({
        storeId,
        funnelId: funnelId ?? undefined,
        checkoutUrl: checkoutUrl ?? undefined,
      });
    },
    [checkoutUrl, funnelId, storeId],
  );

  const handleInlineFrameLoad = React.useCallback(() => {
    inlineFrame.handleLoad();
    if (previewUrl) trackPreviewShown(previewUrl);
  }, [inlineFrame, previewUrl, trackPreviewShown]);

  const showIframeBlocked = inlineFrame.isBlocked && !inlineFrame.isLoading;

  return (
    <div className="flex min-w-0 flex-col gap-3 rounded-xl border border-[color-mix(in_oklch,var(--border)_82%,transparent)] bg-[color-mix(in_oklch,var(--card)_96%,var(--muted))] p-3.5">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <p className="text-muted-foreground m-0 text-[0.6875rem] font-semibold uppercase tracking-[0.04em]">
          {t(`${xp}.previewLiveLabel`)}
        </p>

        {showLiveIframe ? (
          <div className="flex flex-wrap items-center gap-1.5">
            <div
              role="group"
              aria-label={t(`${xp}.viewport.label`)}
              className="inline-flex h-8 items-center gap-0.5 rounded-lg border border-[color-mix(in_oklch,var(--border)_88%,transparent)] bg-[color-mix(in_oklch,var(--muted)_30%,var(--card))] p-0.5"
            >
              <button
                type="button"
                aria-pressed={!isMobile}
                onClick={() => setViewport('web')}
                className={cn(VIEWPORT_BUTTON_BASE, isMobile ? VIEWPORT_BUTTON_INACTIVE : VIEWPORT_BUTTON_ACTIVE)}
              >
                <Monitor className="size-3.5" strokeWidth={2} aria-hidden />
                {t(`${xp}.viewport.web`)}
              </button>
              <button
                type="button"
                aria-pressed={isMobile}
                onClick={() => setViewport('mobile')}
                className={cn(VIEWPORT_BUTTON_BASE, isMobile ? VIEWPORT_BUTTON_ACTIVE : VIEWPORT_BUTTON_INACTIVE)}
              >
                <Smartphone className="size-3.5" strokeWidth={2} aria-hidden />
                {t(`${xp}.viewport.mobile`)}
              </button>
            </div>

            {previewUrl ? (
              <a
                href={previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-card text-foreground inline-flex h-8 items-center gap-1.5 rounded-lg border border-[color-mix(in_oklch,var(--border)_88%,transparent)] px-2.5 text-[0.6875rem] font-semibold no-underline transition-[background-color,border-color,opacity] duration-150 hover:enabled:border-[color-mix(in_oklch,var(--primary)_35%,var(--border))] hover:enabled:bg-[var(--crm-selection-bg)]"
              >
                <ExternalLink className="size-3.5" strokeWidth={2} aria-hidden />
                {t(`${previewXp}.openTab`)}
              </a>
            ) : null}
          </div>
        ) : null}
      </div>

      <div
        className={cn(
          'relative flex h-[34rem] items-center justify-center overflow-hidden rounded-[0.75rem] border border-[color-mix(in_oklch,var(--border)_84%,transparent)] bg-[color-mix(in_oklch,var(--muted)_12%,var(--background))] md:h-[42rem]',
          isMobile &&
            showLiveIframe &&
            'bg-[radial-gradient(color-mix(in_oklch,var(--foreground)_7%,transparent)_1.1px,transparent_1.1px)] p-4 [background-size:18px_18px] sm:p-6',
        )}
      >
        {showLiveIframe && previewUrl ? (
          <div
            className={cn(
              'relative h-full overflow-hidden bg-card transition-[width,border-radius,box-shadow] duration-300 ease-out motion-reduce:transition-none',
              isMobile
                ? 'max-w-full rounded-[2rem] border-[10px] border-[color-mix(in_oklch,var(--foreground)_12%,var(--card))] shadow-[0_28px_64px_-24px_color-mix(in_oklch,var(--foreground)_38%,transparent)] ring-1 ring-[color-mix(in_oklch,var(--foreground)_6%,transparent)]'
                : 'w-full',
            )}
            style={isMobile ? { width: MOBILE_PREVIEW_WIDTH } : undefined}
          >
            <CheckoutPreviewFrame
              previewUrl={previewUrl}
              title={t(`${previewXp}.iframeTitle`)}
              className="bg-card block h-full w-full border-0"
              onLoad={handleInlineFrameLoad}
            />

            {inlineFrame.isLoading ? (
              <div
                className="text-muted-foreground absolute inset-0 z-[2] flex flex-col items-center justify-center gap-2.5 bg-[color-mix(in_oklch,var(--background)_78%,transparent)] text-[0.8125rem] font-medium backdrop-blur-[2px]"
                role="status"
                aria-live="polite"
              >
                <Loader2 className="text-primary size-5 animate-spin" aria-hidden />
                <span>{t(`${xp}.previewLoading`)}</span>
              </div>
            ) : null}

            {showIframeBlocked ? (
              <a
                href={previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-card text-foreground absolute bottom-3 right-3 z-[3] inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[0.6875rem] font-semibold no-underline shadow-[0_4px_16px_color-mix(in_oklch,var(--foreground)_12%,transparent)]"
              >
                <ExternalLink className="size-4" aria-hidden />
                {t(`${previewXp}.openTab`)}
              </a>
            ) : null}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center gap-3 px-6 text-center">
            <div className="flex size-12 items-center justify-center rounded-full bg-[color-mix(in_oklch,var(--primary)_10%,var(--card))]">
              {isSyncing ? (
                <Loader2 className="text-primary size-5 animate-spin" aria-hidden />
              ) : (
                <Monitor className="text-primary size-5" strokeWidth={2} aria-hidden />
              )}
            </div>
            <div className="space-y-1">
              <p className="text-foreground m-0 text-sm font-semibold">
                {t(`${xp}.previewPlaceholderTitle`)}
              </p>
              <p className="text-muted-foreground m-0 text-xs">
                {!isLaunched
                  ? t(`${xp}.previewAwaitLaunch`)
                  : isSyncing
                    ? t(`${previewXp}.importingProducts`)
                    : t(`${xp}.previewPlaceholderPickProduct`)}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
